import ClearIcon from '@mui/icons-material/Clear';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Button,
  CardContent,
  CardHeader,
  CardMedia,
  FormControl,
  Grid,
  IconButton,
  List,
  ListItem,
  MenuItem,
  Link as MuiLink,
  Select,
  Tab,
  Tabs,
} from '@mui/material';
import { common, green, red, yellow } from '@mui/material/colors';
import { nanoid } from '@reduxjs/toolkit';
import clsx from 'clsx';
import { isEmpty, isEqual, uniqWith } from 'lodash';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useState } from 'react';
import { Edit2, MapPin, RotateCcw } from 'react-feather';
import LinesEllipsis from 'react-lines-ellipsis';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { imageBase, sitesClient } from '../../api/client';
import { CardHeaderFlex } from '../../component/cardHeaderFlex/CardHeaderFlex';
import { ConfirmDialog } from '../../component/confirmDialog/ConfirmDialog';
import DependenciesButton from '../../component/dependenciesButton/DependenciesButton';
import { InfoPopup } from '../../component/infoPopup/InfoPopup';
import KPI from '../../component/kpi/KPI';
import LinearProgress from '../../component/linearProgress/LinearProgress';
import ListTypography from '../../component/listTypography/ListTypography';
import { CommonMap } from '../../component/map/Common';
import { StreetView } from '../../component/map/StreetView';
import { LocationVisits } from '../../component/site/LocationVisits';
import { MapEditorModal } from '../../component/site/MapEditorModal';
import { MergeSiteForm } from '../../component/site/MergeSiteForm';
import { ReadyForPickupModal } from '../../component/site/ReadyForPickupModal';
import { RecallForm } from '../../component/site/RecallForm';
import SiteForm from '../../component/site/SiteForm';
import { SiteJobForm } from '../../component/site/SiteJobForm';
import { SiteNotesForm } from '../../component/site/SiteNotesForm';
import { StockInstalled } from '../../component/site/StockInstalled';
import { ToolsInUse } from '../../component/site/ToolsInUse';
import Skeleton from '../../component/skeleton/Skeleton';
import TabPanel from '../../component/tabPanel/TabPanel';
import { JobsiteActivityTimeline, JobsiteTimelineEvent } from '../../component/timeline/JobsiteActivityTimeline';
import { getSiteAddress } from '../../helpers/dataHelper';
import prepareParams from '../../helpers/prepareParams';
import useBooleanKeys from '../../hooks/useBooleanKeys';
import { useIsRemediation } from '../../hooks/useIsRemediation';
import { useMarketTerm } from '../../hooks/useMarketTerm';
import useSearchParams from '../../hooks/useSearchParams';
import useTablePagination from '../../hooks/useTablePagination';
import { getSiteActivityTimeline, getSiteActivityTimelineSelector } from '../../redux/slices/assignments/assignmentsSlice';
import { getImagesByOwnerId, listImagesByOwnerId } from '../../redux/slices/images/imagesSlice';
import { getLocationVisits, getLocationVisitsById } from '../../redux/slices/locationVisits/locationVisitsSlice';
import {
  getSiteByUuid,
  getSiteByUuidFetching,
  getSiteDependencies,
  getSiteDependenciesById,
  getSiteDependenciesFetching,
  getSiteDetailsById,
  getSiteEquipmentSummary,
  getSiteEquipmentSummaryById,
  getSiteEquipmentSummaryFetching,
  getSiteKPIById,
  getSiteKpis,
  removeSite,
  setSiteGeofence,
  setSiteReadyForPickup,
  siteKpisFetching,
  updateSite,
} from '../../redux/slices/sites/sitesSlice';
import { getProductToolsById, listProductTools, productToolsFetching } from '../../redux/slices/tools/toolsSlice';
import { getAllUsers, getUserAbilities, getUserData, isAdminUserSelector, listUsers } from '../../redux/slices/users/usersSlice';
import { useDispatch } from '../../redux/store';
import { Card, CardActions, CardContentKpi, CardFlex, CardHeaderTabs, GridCells, MediaHolder, Typography } from '../../styled/components';
import { viewerTimeZone } from '../dashboard/kpiWindow';
import { ToolUsageContainer } from '../locationVisits/ToolUsageContainer';
import { Reports } from '../reporting/Reports';
import { Rooms } from '../rooms/Rooms';
import { Available } from '../tools/Available';
import { AssignedEquipment } from './AssignedEquipment';
import { BoundaryTypeEnum, SitesProps } from './interface';

const urlByType = {
  customer: '/customers/job-sites',
  organization: '/company/sites',
  recycle: '/admin/sites/recycle',
};

export const Site: React.FC<SitesProps> = ({ type = 'customer' }: SitesProps) => {
  const dispatch = useDispatch();
  const { id, tab = 0 } = useParams();
  const navigate = useNavigate();
  const details = useSelector(getSiteDetailsById(id));
  const fetching = useSelector(getSiteByUuidFetching);
  const imageKeys = useSelector(getImagesByOwnerId(id));
  const [params] = useSearchParams();
  const queryParams = useMemo(
    () =>
      Object.keys(params).reduce((partParams, key) => ({ ...partParams, [key]: prepareParams[key] ? prepareParams[key](params[key]) : params[key] }), {
        siteId: id,
      }),
    [params, id],
  );
  const locationVisits = useSelector(getLocationVisitsById(queryParams));
  const siteKPI = useSelector(getSiteKPIById(id));
  const fetchingSiteKPI = useSelector(siteKpisFetching);
  const equipmentSummary = useSelector(getSiteEquipmentSummaryById(id));
  const fetchingEquipmentSummary = useSelector(getSiteEquipmentSummaryFetching);
  const equipmentCounts = useMemo(() => {
    const counts = { blower: 0, dehumidifier: 0, airScrubber: 0, other: 0 };
    (equipmentSummary?.rows ?? []).forEach((row) => {
      if (!row.category || row.category === 'Total') return;
      const key = row.category.toLowerCase();
      if (key === 'blower') counts.blower += row.count ?? 0;
      else if (key === 'dehumidifier') counts.dehumidifier += row.count ?? 0;
      else if (key === 'air scrubber') counts.airScrubber += row.count ?? 0;
      else counts.other += row.count ?? 0;
    });
    return counts;
  }, [equipmentSummary]);
  const [pagination, paginationToGet, paginationControl] = useTablePagination({ paginationDirty: locationVisits?.pagination });
  const [modals, toggleModal] = useBooleanKeys({
    showEditForm: false,
    showMergeForm: false,
    showMapEditor: false,
    showRecall: false,
    showNotesForm: false,
    showJobForm: false,
    showReadyForPickup: false,
    showClearReady: false,
  });
  const dependencies = useSelector(getSiteDependenciesById(id));
  const fetchingDependencies = useSelector(getSiteDependenciesFetching);
  const [deleting, setDeleting] = useState(false);
  const [popupOpen, setPopupOpen] = useState('');
  const tabValue = Number(tab);
  const isAdminUser = useSelector(isAdminUserSelector);
  const abilities = useSelector(getUserAbilities);
  const userData = useSelector(getUserData);
  const orgUsers = useSelector(getAllUsers({ organizationId: userData?.organization_id || '' }));
  const isRemediation = useIsRemediation();
  const term = useMarketTerm();
  const [, , clearParams] = useSearchParams();
  const [toolsKey, setToolsKey] = useState(nanoid());
  const paramsToUse = useMemo(
    () => ({
      trackingSiteId: id,
      productTypeId: '1',
      scanStatus: '2,3',
      _checkOnly: '1',
      offset: 0,
      limit: 1, // Just need to check if we have some tools in use
    }),
    [id],
  );
  const tools = useSelector(getProductToolsById(paramsToUse));
  const fetchingTools = useSelector(productToolsFetching);

  const recycleNotAdmin = details?.site_type_id === 1000 && !isAdminUser;
  const isInactive = details?.active === false;
  // Remediation job sites get the timeline-first layout: Status box (activity
  // timeline + Left Onsite) in col 2 row 1, street view moved to col 1 row 2.
  const useTimelineLayout = type === 'customer' && isRemediation && !recycleNotAdmin;
  const timelineEvents = useSelector(getSiteActivityTimelineSelector(id));
  const tz = useMemo(() => viewerTimeZone(), []);
  // Cleared = the date the site was fully cleared: nothing still on site (the
  // Equipment Summary counts scan_status 2,3 / product_type 1, matching the
  // billing report) AND at least one recovery. Otherwise there is no cleared
  // date. Same rule as the billing report's "Cleared" line.
  const onSiteEquipmentTotal = equipmentCounts.blower + equipmentCounts.dehumidifier + equipmentCounts.airScrubber + equipmentCounts.other;
  const clearedDate = useMemo(() => {
    if (onSiteEquipmentTotal > 0) return undefined;
    const recoveryTimes = (timelineEvents ?? []).filter((event) => event.type === 'recovery' && event.timestamp).map((event) => event.timestamp as string);
    return recoveryTimes.length ? recoveryTimes.reduce((a, b) => (new Date(b).getTime() > new Date(a).getTime() ? b : a)) : undefined;
  }, [timelineEvents, onSiteEquipmentTotal]);
  // Delivery/recovery nodes labeled with per-category counts in Blower -
  // Dehumidifier - Air Scrubber - Other order; the tooltip spells them out.
  // A recovery the backend flagged as a Return (same day) or a Cancellation
  // (within two hours) keeps its own node. The icon's line colour says which
  // kind of event it is.
  const activityEvents = useMemo((): JobsiteTimelineEvent[] => {
    const verbs: Record<string, { one: string; many: string; color: string }> = {
      delivery: { one: 'Delivered', many: 'Deliveries', color: common.black },
      recovery: { one: 'Recovered', many: 'Recoveries', color: green[600] },
      return: { one: 'Returned', many: 'Returns', color: yellow[700] },
      cancellation: { one: 'Cancelled', many: 'Cancellations', color: red[600] },
    };
    const assignedTab = `${urlByType[type]}/${id}/details/4`;
    return (timelineEvents ?? [])
      .filter((event) => event.timestamp && event.type)
      .map((event) => {
        const counts = [
          { label: 'Blower', amount: event.blower_cnt ?? 0 },
          { label: 'Dehumidifier', amount: event.dehumidifier_cnt ?? 0 },
          { label: 'Air Scrubber', amount: event.air_scrubber_cnt ?? 0 },
          { label: 'Other', amount: event.other_cnt ?? 0 },
        ];
        const visitHref = event.location_visit_id ? `/activity/site-visits/${event.location_visit_id}/details` : undefined;
        const isDelivery = event.type === 'delivery';
        const eventCnt = event.event_cnt ?? 1;
        const kind = verbs[event.type ?? ''] ?? verbs.recovery;
        const verb = eventCnt > 1 ? `${eventCnt} ${kind.many}` : kind.one;
        return {
          id: event.id ?? '',
          timestamp: event.timestamp ?? '',
          type: event.type ?? '',
          // two lines: verb, then the category counts; the component adds the date line
          label: `${verb}\n${counts.map((entry) => entry.amount).join('-')}`,
          icon: isDelivery ? <MapPin size={18} /> : <RotateCcw size={18} />,
          color: kind.color,
          href: isDelivery ? visitHref ?? assignedTab : visitHref,
          tooltip: (
            <Box component="table" sx={{ borderCollapse: 'collapse', '& td, & th': { px: 1, py: 0.25 } }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.4)' }}>
                  <th align="left">Category</th>
                  <th align="right">Count</th>
                </tr>
              </thead>
              <tbody>
                {counts.map((entry) => (
                  <tr key={entry.label}>
                    <td>{entry.label}</td>
                    <td align="right">{entry.amount}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: '1px solid rgba(255, 255, 255, 0.4)' }}>
                  <td>
                    <strong>Total</strong>
                  </td>
                  <td align="right">
                    <strong>{event.item_cnt ?? counts.reduce((sum, entry) => sum + entry.amount, 0)}</strong>
                  </td>
                </tr>
              </tbody>
            </Box>
          ),
        };
      });
  }, [timelineEvents, id, type]);
  const handleChangeTab = (event, newValue) => {
    navigate(`${urlByType[type]}/${id}/details/${newValue}${clearParams}`);
  };

  useEffect(() => {
    if (paramsToUse.trackingSiteId) {
      dispatch(listProductTools(paramsToUse));
    }
  }, [dispatch, paramsToUse]);

  useEffect(() => {
    if (toolsKey) {
      dispatch(
        getLocationVisits({
          ...queryParams,
          ...paginationToGet,
        }),
      );
    }
  }, [dispatch, queryParams, paginationToGet, toolsKey]);

  useEffect(() => {
    if (id && toolsKey) {
      dispatch(listImagesByOwnerId(id));
      dispatch(getSiteByUuid(id));
      if (abilities.can('CRUD')) {
        dispatch(getSiteDependencies(id));
      }
      if (abilities.can('Container')) {
        dispatch(getSiteKpis(id));
      }
      if (type === 'customer' && isRemediation) {
        dispatch(getSiteEquipmentSummary(id));
        dispatch(getSiteActivityTimeline({ siteId: id, tz }));
      }
    }
  }, [dispatch, id, isAdminUser, abilities, toolsKey, type, isRemediation, tz]);

  const address = getSiteAddress(details).map((item, index) => <div key={index.toString()}>{item}</div>);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      if (dependencies?.connected_record_count && dependencies?.connected_record_count > 0) {
        const payload = {
          id: details?.id,
          organization_id: details?.organization_id,
          name: details?.name,
          latitude: details?.latitude?.toString(),
          longitude: details?.longitude?.toString(),
          address_1: details?.address1,
          city: details?.city,
          state: details?.state,
          postal: details?.postal,
          site_type_id: details?.site_type_id,
          active: false,
        };

        await dispatch(updateSite(payload)).unwrap();
      } else if (details?.id) {
        await sitesClient.deleteSite(details.id);
        dispatch(removeSite(details.id));
      }

      navigate(isAdminUser && details?.organization_id ? `/admin/organizations/organizations/${details.organization_id}/details/2` : urlByType[type]);
    } catch (error) {
      setDeleting(false);
      console.log(error); // eslint-disable-line
    }
  };
  const disableDelete = fetching || fetchingDependencies;

  const [settingBoundary, setSettingBoundary] = useState(false);
  const handleDeleteBoundary = async () => {
    if (!id) return;
    setSettingBoundary(true);
    try {
      // Empty coordinates clears coordinates_geo on the backend (boundary -> Center).
      await dispatch(setSiteGeofence({ siteId: id, geofence: { coordinates: [] } })).unwrap();
      await dispatch(getSiteByUuid(id)).unwrap();
    } catch (error) {
      console.log(error); // eslint-disable-line
    } finally {
      setSettingBoundary(false);
    }
  };

  // Inline project-manager edit: an edit button swaps the value for a dropdown
  // with Confirm/Cancel. Escape and leaving the page cancel (local state).
  const [editingPM, setEditingPM] = useState(false);
  const [pmValue, setPmValue] = useState('');
  const [savingPM, setSavingPM] = useState(false);
  const startEditPM = () => {
    if (userData?.organization_id) dispatch(listUsers({ organizationId: userData.organization_id, limit: 1000 }));
    setPmValue(details?.project_manager_id ?? '');
    setEditingPM(true);
  };
  const cancelEditPM = () => setEditingPM(false);
  const saveEditPM = async () => {
    if (!details || !details.id) return;
    setSavingPM(true);
    try {
      await dispatch(
        updateSite({
          id: details.id,
          organization_id: details.organization_id,
          customer_id: details.customer_id,
          name: details.name,
          latitude: details.latitude?.toString(),
          longitude: details.longitude?.toString(),
          address_1: details.address1,
          city: details.city,
          state: details.state,
          postal: details.postal,
          site_type_id: details.site_type_id,
          active: details.active,
          notes: details.notes ?? undefined,
          project_manager_id: pmValue || undefined,
        }),
      ).unwrap();
      setEditingPM(false);
    } catch (error) {
      console.log(error); // eslint-disable-line
    } finally {
      setSavingPM(false);
    }
  };
  // Escape cancels the edit; navigating to a different site cancels it too.
  useEffect(() => {
    if (!editingPM) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') cancelEditPM();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [editingPM]);
  useEffect(() => {
    setEditingPM(false);
    // Opening/switching a job site starts at the top of the page.
    window.scrollTo({ top: 0 });
  }, [id]);

  // Clearing readiness un-marks the site and drops any scheduled pickup date.
  const [clearingReady, setClearingReady] = useState(false);
  const handleClearReady = async () => {
    if (!id) return;
    setClearingReady(true);
    try {
      await dispatch(setSiteReadyForPickup({ siteId: id, body: { ready_for_pickup: false, pickup_date: undefined } })).unwrap();
      toggleModal('showClearReady');
    } catch (error) {
      console.log(error); // eslint-disable-line
    } finally {
      setClearingReady(false);
    }
  };

  const detailsData = [
    {
      label: 'Name',
      value: details?.name,
    },
    ...(!recycleNotAdmin && !isRemediation
      ? [
          {
            label: 'Customer',
            value: (
              <React.Fragment>
                {type === 'customer' &&
                  (details?.customer_id ? <Link to={`/customers/customers/${details?.customer_id}/details`}>{details?.customer?.name}</Link> : 'Unassigned')}
                {type === 'organization' && (details?.site_type?.name || '')}
              </React.Fragment>
            ),
          },
        ]
      : []),
    {
      label: 'Address',
      ownValue: true,
      value: (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 3 }}>
          <Box>{address}</Box>
          {!recycleNotAdmin && (
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
              <Typography variant="body2" color="text.secondary">
                {details?.boundary_type ? BoundaryTypeEnum[details?.boundary_type] : BoundaryTypeEnum.center}
              </Typography>
              {abilities.can('CRUD') &&
                (details?.boundary_type === 'geofence' ? (
                  <Button size="small" variant="outlined" color="error" disabled={fetching || isInactive || settingBoundary} onClick={handleDeleteBoundary}>
                    Delete
                  </Button>
                ) : (
                  <Button size="small" variant="outlined" color="primary" disabled={fetching || isInactive} onClick={() => toggleModal('showMapEditor')}>
                    Set
                  </Button>
                ))}
            </Box>
          )}
        </Box>
      ),
    },
    {
      label: 'Project Manager',
      value: editingPM ? (
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <Select value={pmValue} onChange={(e) => setPmValue(String(e.target.value))} displayEmpty autoFocus>
            <MenuItem value="">— Unassigned —</MenuItem>
            {(orgUsers?.result ?? []).map((user) => (
              <MenuItem key={user.id} value={user.id}>
                {`${user.last_name}, ${user.first_name}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        details?.project_manager || '—'
      ),
      // Inline PM edit is Remediation-only, so other vertical markets see this row
      // exactly as before (value, no edit control).
      action:
        isRemediation && abilities.can('CRUD') && !isInactive ? (
          editingPM ? (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="contained" color="primary" disabled={savingPM} onClick={saveEditPM}>
                Confirm
              </Button>
              <Button size="small" variant="outlined" color="inherit" disabled={savingPM} onClick={cancelEditPM}>
                Cancel
              </Button>
            </Box>
          ) : (
            <IconButton size="small" aria-label="Edit project manager" onClick={startEditPM}>
              <Edit2 size={16} />
            </IconButton>
          )
        ) : undefined,
    },
    {
      // Client job number. Mirrors the Project Manager row: the value (or the
      // page's em-dash placeholder) is always shown, and the pencil appears only
      // for Remediation editors. Editing opens a popup rather than an inline
      // control because a job carries four fields, not one.
      label: 'Job #',
      value: details?.job_number || '—',
      action:
        isRemediation && abilities.can('CRUD') && !isInactive ? (
          <IconButton size="small" aria-label="Edit job number" onClick={() => toggleModal('showJobForm')}>
            <Edit2 size={16} />
          </IconButton>
        ) : undefined,
    },
    {
      label: type === 'customer' && isRemediation ? 'Created / Cleared / Pickup' : 'Created',
      value:
        type === 'customer' && isRemediation ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>{details?.created_at ? DateTime.fromISO(details?.created_at).toLocaleString(DateTime.DATE_MED) : '—'}</span>
            <Box component="span" sx={{ opacity: 0.4 }}>
              /
            </Box>
            <span>{clearedDate ? DateTime.fromISO(clearedDate).toLocaleString(DateTime.DATE_MED) : '—'}</span>
            <Box component="span" sx={{ opacity: 0.4 }}>
              /
            </Box>
            {details?.ready_for_pickup === true ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {abilities.can('CRUD') && !isInactive ? (
                  <MuiLink component="button" type="button" onClick={() => toggleModal('showReadyForPickup')}>
                    {details?.pickup_date ? DateTime.fromISO(details.pickup_date).toLocaleString(DateTime.DATE_MED) : 'Unscheduled'}
                  </MuiLink>
                ) : (
                  <span>{details?.pickup_date ? DateTime.fromISO(details.pickup_date).toLocaleString(DateTime.DATE_MED) : 'Unscheduled'}</span>
                )}
                {abilities.can('CRUD') && !isInactive && (
                  <IconButton size="small" aria-label="Clear ready for pickup" onClick={() => toggleModal('showClearReady')}>
                    <ClearIcon fontSize="inherit" />
                  </IconButton>
                )}
              </Box>
            ) : abilities.can('CRUD') && !isInactive ? (
              <Button size="small" variant="outlined" color="primary" disabled={fetching || isInactive} onClick={() => toggleModal('showReadyForPickup')}>
                Set Ready
              </Button>
            ) : (
              <span>—</span>
            )}
          </Box>
        ) : details?.created_at ? (
          DateTime.fromISO(details?.created_at).toLocaleString(DateTime.DATE_MED)
        ) : (
          ''
        ),
    },
    ...(type === 'customer'
      ? [
          {
            label: 'Notes',
            value: details?.notes ? (
              <Box sx={{ position: 'relative', pr: abilities.can('CRUD') && !isInactive ? '56px' : 0 }}>
                <Box sx={{ whiteSpace: 'pre-wrap' }}>
                  <LinesEllipsis text={details.notes} maxLine={3} />
                </Box>
                {abilities.can('CRUD') && !isInactive && (
                  <MuiLink component="button" type="button" onClick={() => toggleModal('showNotesForm')} sx={{ position: 'absolute', top: 0, right: 0 }}>
                    Edit
                  </MuiLink>
                )}
              </Box>
            ) : abilities.can('CRUD') && !isInactive ? (
              <MuiLink component="button" type="button" onClick={() => toggleModal('showNotesForm')}>
                No job site notes
              </MuiLink>
            ) : (
              '—'
            ),
          },
        ]
      : []),
  ];

  const handleCloseMergeSite = (targetSiteId?: string) => {
    toggleModal('showMergeForm');
    if (targetSiteId) {
      navigate(`${isAdminUser ? '/admin/organizations/organizations' : urlByType[type]}/${targetSiteId}/details`, { replace: true });
    }
  };

  const tabIndexAddon = type === 'organization' ? 1 : 0;

  const center = {
    lat: details?.latitude ?? 0,
    lng: details?.longitude ?? 0,
  };

  const cardTitle = recycleNotAdmin ? 'Recycle Site' : 'Job Site';

  const coordinatesKey = uniqWith(details?.coordinates, isEqual)
    .map((loc) => `${loc.latitude},${loc.longitude}`)
    .join('|');

  const imageCard = (
    <Grid item xs={12} sm={6}>
      <CardFlex style={{ display: 'flex', flexDirection: 'column' }}>
        <CardHeaderFlex title={imageKeys[0] === 'default-job-site.png' ? 'Street View (Closest to location)' : 'Site'} legend="Image Source" />
        {imageKeys[0] === 'default-job-site.png' ? (
          <StreetView key={`${details?.latitude}_${details?.longitude}`} zoom={17} lat={details?.latitude || 0} lng={details?.longitude || 0} />
        ) : (
          <MediaHolder className={clsx({ icon: imageKeys.length === 0 })} sx={{ p: 4 }}>
            {imageKeys.length > 0 ? (
              <CardMedia component="img" image={imageBase(imageKeys[0])} alt={details?.name} style={{ objectFit: 'contain' }} />
            ) : (
              <ImageOutlinedIcon sx={{ fontSize: '72px', opacity: 0.1 }} />
            )}
          </MediaHolder>
        )}
      </CardFlex>
    </Grid>
  );

  const currentStatusCard = (
    <Grid item xs={12} sm={6}>
      <CardFlex>
        <CardHeaderFlex title={abilities.can('Container') && type !== 'customer' ? 'Container Metrics' : 'Current'} legend="Status" />
        {abilities.can('Container') && type !== 'customer' ? (
          <CardContentKpi>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <KPI
                  title="Containers"
                  display="inline-block"
                  data={[
                    {
                      amount: siteKPI?.containers_available || 0,
                      label: 'Available',
                      color: 'black',
                    },
                  ]}
                  background="grey"
                  fetching={fetchingSiteKPI}
                />
              </Grid>
            </Grid>
          </CardContentKpi>
        ) : (
          <React.Fragment>
            {type === 'organization' ? (
              <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ fontSize: 40 }}>No Tracking</Typography>
              </CardContent>
            ) : (
              <CardContentKpi>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <KPI
                      title="Tool Usage"
                      display="inline-block"
                      data={[
                        {
                          amount: details?.tools_used_cnt || 0,
                          label: 'Usage',
                          color: 'black',
                        },
                        {
                          amount: details?.tools_left_behind_cnt || 0,
                          label: term('Left Onsite'),
                          color: details?.tools_left_behind_cnt && details?.tools_left_behind_cnt > 0 ? 'red' : 'black',
                        },
                      ]}
                      background={details?.tools_left_behind_cnt && details?.tools_left_behind_cnt > 0 ? 'pink' : 'grey'}
                      fetching={fetching}
                    />
                  </Grid>
                  {type === 'customer' && isRemediation && (
                    <Grid item xs={12} md={6}>
                      <KPI
                        title={term('Left Onsite')}
                        display="inline-block"
                        data={[
                          { amount: equipmentCounts.blower, label: 'Blower', color: 'black' },
                          { amount: equipmentCounts.dehumidifier, label: 'Dehum', color: 'black' },
                          { amount: equipmentCounts.airScrubber, label: 'Air Scr', color: 'black' },
                          { amount: equipmentCounts.other, label: 'Other', color: 'black' },
                        ]}
                        background="grey"
                        fetching={fetchingEquipmentSummary}
                      />
                    </Grid>
                  )}
                  {abilities.can('Stock') && (
                    <Grid item xs={12} md={6}>
                      <KPI
                        title="Stock Activity"
                        display="inline-block"
                        data={[
                          {
                            amount: details?.stock_assigned_cnt || 0,
                            label: 'Assigned',
                            color: 'black',
                          },
                          {
                            amount: details?.stock_on_site_cnt || 0,
                            label: 'OnSite',
                            color: 'black',
                          },
                          {
                            amount: details?.stock_installed_cnt || 0,
                            label: 'Installed',
                            color: 'black',
                          },
                          {
                            amount: details?.stock_error_cnt || 0,
                            label: 'Error',
                            color: 'black',
                          },
                        ]}
                        background={details?.stock_error_cnt && details?.stock_error_cnt > 0 ? 'pink' : 'grey'}
                        fetching={fetching}
                      />
                    </Grid>
                  )}
                </Grid>
                <Skeleton
                  width={200}
                  fetching={fetching}
                  value={
                    details?.active && details?.vehicles ? (
                      <Box>
                        Active Vehicles:{' '}
                        {details?.vehicleLinkMapped?.map((vehicle, index) => (
                          <React.Fragment key={vehicle[1]}>
                            {index > 0 ? `, ` : ''}
                            <strong>
                              {vehicle[0] && vehicle[0] !== '00000000-0000-0000-0000-000000000000' ? (
                                <Link to={`/company/vehicles/${vehicle[0]}/details`}>{vehicle[1]}</Link>
                              ) : (
                                vehicle[1]
                              )}
                            </strong>
                          </React.Fragment>
                        ))}
                      </Box>
                    ) : (
                      ''
                    )
                  }
                />
              </CardContentKpi>
            )}
          </React.Fragment>
        )}
        {abilities.can('Recall-Job') && (
          <CardActions>
            <Button
              sx={{ minWidth: 80 }}
              variant="contained"
              color="primary"
              disabled={fetching || fetchingTools || isEmpty(tools?.result) || isInactive}
              onClick={() => toggleModal('showRecall')}
            >
              Recall
            </Button>
          </CardActions>
        )}
      </CardFlex>
    </Grid>
  );

  const timelineStatusCard = (
    <Grid item xs={12} sm={6}>
      <CardFlex style={{ display: 'flex', flexDirection: 'column' }}>
        <CardHeaderFlex title="Jobsite Activity" legend="Status" />
        <CardContentKpi sx={{ flexGrow: 1 }}>
          <JobsiteActivityTimeline events={activityEvents} />
        </CardContentKpi>
        {/* bottom row: onsite summary inline with the Recall button */}
        <CardActions sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 4, rowGap: 1, flexGrow: 1, pl: 2, fontSize: '0.99rem' }}>
            <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontSize: 'inherit', fontWeight: 'bold', fontStyle: 'italic' }}>
              {term('Left Onsite')}
            </Typography>
            {[
              { label: 'Blower', amount: equipmentCounts.blower },
              { label: 'Dehumidifier', amount: equipmentCounts.dehumidifier },
              { label: 'Air Scrubber', amount: equipmentCounts.airScrubber },
              { label: 'Other', amount: equipmentCounts.other },
            ].map((entry) => (
              <Typography key={entry.label} variant="body1" sx={{ whiteSpace: 'nowrap', fontSize: 'inherit' }}>
                <strong>{entry.label}</strong> {entry.amount}
              </Typography>
            ))}
            <Typography variant="body1" sx={{ whiteSpace: 'nowrap', fontSize: 'inherit' }}>
              <strong>Total</strong> {equipmentCounts.blower + equipmentCounts.dehumidifier + equipmentCounts.airScrubber + equipmentCounts.other}
            </Typography>
          </Box>
          {abilities.can('Recall-Job') && (
            <Button
              sx={{ minWidth: 80 }}
              variant="contained"
              color="primary"
              disabled={fetching || fetchingTools || isEmpty(tools?.result) || isInactive}
              onClick={() => toggleModal('showRecall')}
            >
              Recall
            </Button>
          )}
        </CardActions>
      </CardFlex>
    </Grid>
  );

  return (
    <React.Fragment>
      {fetching && <LinearProgress />}
      <GridCells container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6}>
          <CardFlex sx={{ position: 'relative' }}>
            <Box sx={{ ...(details && details?.active === false && { filter: 'grayscale(1)', opacity: 0.55 }) }}>
              <CardHeaderFlex title={type === 'organization' ? 'Company Site' : cardTitle} legend="Details" />
              <CardHeader
                action={
                  <Grid container display="flex" justifyContent="flex-end">
                    {abilities.can('CRUD') && !recycleNotAdmin && (
                      <Grid item xs={12} md="auto" display="flex" flexDirection="column" justifyContent="flex-end" sx={{ pb: 2 }}>
                        <Button onClick={() => toggleModal('showEditForm')} variant="contained" color="primary" disabled={fetching} sx={{ minWidth: 80 }}>
                          Edit
                        </Button>
                        {type === 'customer' && (
                          <Button
                            onClick={() => toggleModal('showMergeForm')}
                            variant="contained"
                            color="warning"
                            disabled={fetching || !details?.active}
                            sx={{ minWidth: 80, mt: 2 }}
                          >
                            Merge
                          </Button>
                        )}
                      </Grid>
                    )}
                    {abilities.can('CRUD') && (
                      <Grid item xs={12} md="auto" display="flex" justifyContent="flex-end">
                        <DependenciesButton
                          disableDelete={disableDelete || isInactive}
                          dependencies={dependencies?.connected_record_count}
                          active={details?.active}
                          deleting={deleting}
                          name={details?.name}
                          handleDelete={handleDelete}
                          skipArchive
                        />
                      </Grid>
                    )}
                  </Grid>
                }
                title={<ListTypography data={detailsData} fetching={fetching} labelXl={3} />}
              />
            </Box>
            {details && details?.active === false && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    transform: 'rotate(-20deg)',
                    fontSize: 96,
                    fontWeight: 800,
                    letterSpacing: 4,
                    color: 'rgba(230, 57, 70, 0.18)',
                    textTransform: 'uppercase',
                    userSelect: 'none',
                    border: '6px solid rgba(230, 57, 70, 0.18)',
                    px: 4,
                    py: 1,
                    borderRadius: 2,
                  }}
                >
                  Disabled
                </Box>
              </Box>
            )}
          </CardFlex>
        </Grid>
        {useTimelineLayout ? timelineStatusCard : imageCard}
        {useTimelineLayout ? imageCard : currentStatusCard}
        <Grid item xs={12} sm={6}>
          <CardFlex>
            <CardHeaderFlex title={details ? `${details?.address1}, ${details?.city}` : ''} legend="Map" />
            {details && (
              <CommonMap
                key={details?.coordinates ? coordinatesKey : 'marker'}
                zoom={17}
                markers={[
                  {
                    key: details?.id || '',
                    lat: details?.latitude || 0,
                    lng: details?.longitude || 0,
                    locationType: details?.site_type_id === 100 ? 'At Job Site' : 'At Home',
                    label: details?.name || '',
                    siteId: details?.id,
                  },
                ]}
                {...(details?.coordinates && { coordinates: details?.coordinates, center })}
                mobileView
                withTrafficFullScreen
              />
            )}
          </CardFlex>
        </Grid>
      </GridCells>

      <Card mb={6}>
        <CardHeaderTabs
          title={
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              {abilities.can('Container') ? (
                <Tabs value={tabValue} onChange={handleChangeTab} variant="scrollable" allowScrollButtonsMobile>
                  <Tab label="Onsite Containers" />
                  <Tab label="Site Visits" />
                  <Tab label="Activity" />
                </Tabs>
              ) : (
                <Tabs value={tabValue} onChange={handleChangeTab} variant="scrollable" allowScrollButtonsMobile>
                  {type !== 'organization' && abilities.can('Stock') && <Tab label="Installed Stock" />}
                  {type === 'organization' && <Tab label="Rooms" />}
                  {type !== 'organization' && <Tab label="Tools In Use" />}

                  <Tab label="Site Visits" />
                  <Tab label="Activity" />
                  {type !== 'organization' && <Tab label="Reports" />}
                  {(abilities.can('Forced Delivery') || abilities.can('Stock')) && <Tab label="Assigned Equipment" />}
                </Tabs>
              )}
            </Box>
          }
          action={
            <IconButton onClick={() => setPopupOpen('tabs')}>
              <InfoOutlinedIcon fontSize="small" color="secondary" />
            </IconButton>
          }
          sx={{
            '& .MuiCardHeader-content': {
              width: 0,
            },
          }}
        />
        <CardContent sx={{ p: 0 }}>
          {abilities.can('Container') ? (
            <React.Fragment>
              <TabPanel value={tabValue} index={0}>
                <Available siteId={id} />
              </TabPanel>
              <TabPanel value={tabValue} index={1}>
                <LocationVisits
                  items={locationVisits}
                  {...(type !== 'organization' ? { noLocation: true, noCustomer: true } : { noUsed: true, noLeftBehind: true, noRecovered: true })}
                  paginationControl={paginationControl}
                  pagination={pagination}
                  withFilters
                />
              </TabPanel>
              <TabPanel value={tabValue} index={2}>
                <ToolUsageContainer withExport={false} siteId={id} withFilters />
              </TabPanel>
            </React.Fragment>
          ) : (
            <React.Fragment>
              {type === 'organization' ? (
                <TabPanel value={tabValue} index={0}>
                  <Rooms siteId={id} />
                </TabPanel>
              ) : (
                <React.Fragment>
                  {abilities.can('Stock') && (
                    <TabPanel value={tabValue} index={0}>
                      <StockInstalled />
                    </TabPanel>
                  )}
                  <TabPanel value={tabValue} index={abilities.can('Stock') ? 1 : 0}>
                    <ToolsInUse key={toolsKey} />
                  </TabPanel>
                </React.Fragment>
              )}
              <TabPanel value={tabValue} index={type === 'organization' || !abilities.can('Stock') ? 1 : 2}>
                <LocationVisits
                  items={locationVisits}
                  {...(type !== 'organization' ? { noLocation: true, noCustomer: true } : { noUsed: true, noLeftBehind: true, noRecovered: true })}
                  paginationControl={paginationControl}
                  pagination={pagination}
                  withFilters
                />
              </TabPanel>
              <TabPanel value={tabValue} index={type === 'organization' || !abilities.can('Stock') ? 2 : 3}>
                <ToolUsageContainer withExport={false} siteId={id} withFilters />
              </TabPanel>
              {type !== 'organization' && (
                <TabPanel value={tabValue} index={abilities.can('Stock') ? 4 : 3}>
                  <Box sx={{ mx: 1 }}>
                    <Reports defaultValues={{ jobSiteId: id ?? '' }} category="job-site" />
                  </Box>
                </TabPanel>
              )}
              {(abilities.can('Forced Delivery') || abilities.can('Stock')) && (
                <TabPanel value={tabValue} index={type == 'organization' ? 3 : (abilities.can('Stock') ? 5 : 4) + tabIndexAddon}>
                  <Box sx={{ mx: 1 }}>
                    <AssignedEquipment siteName={details?.name} siteActive={details?.active} />
                  </Box>
                </TabPanel>
              )}
            </React.Fragment>
          )}
        </CardContent>
      </Card>
      <InfoPopup title="Tab Details" subtitle="Displays the list of visits to the Job Site." onClose={() => setPopupOpen('')} open={popupOpen === 'tabs'}>
        <Card sx={{ mb: 3, p: 3 }}>
          <CardHeader title={<strong>Site Visits</strong>} sx={{ p: 0 }} />
          <CardContent sx={{ p: 0, pt: 2 }} style={{ paddingBottom: 0 }}>
            List of vehicle visits to this job-site with:
            <List sx={{ listStyle: 'disc', pl: 3 }}>
              <ListItem sx={{ display: 'block list-item', padding: '0 4px' }}>Tools used</ListItem>
              <ListItem sx={{ display: 'block list-item', padding: '0 4px' }}>Tools left behind</ListItem>
              <ListItem sx={{ display: 'block list-item', padding: '0 4px' }}>Stock Installed</ListItem>
            </List>
          </CardContent>
        </Card>
      </InfoPopup>
      {abilities.can('CRUD') && <SiteForm open={modals.showEditForm} siteId={id} onClose={() => toggleModal('showEditForm')} type={type} />}
      {abilities.can('CRUD') && <MergeSiteForm open={modals.showMergeForm} siteId={id} onClose={handleCloseMergeSite} />}
      {abilities.can('CRUD') && <MapEditorModal open={modals.showMapEditor} onClose={() => toggleModal('showMapEditor')} siteId={id} />}
      {abilities.can('Recall-Job') && (
        <RecallForm open={modals.showRecall} siteId={id} onClose={() => toggleModal('showRecall')} onSuccess={() => setToolsKey(nanoid())} />
      )}
      {abilities.can('CRUD') && type === 'customer' && <SiteNotesForm open={modals.showNotesForm} siteId={id} onClose={() => toggleModal('showNotesForm')} />}
      {abilities.can('CRUD') && type === 'customer' && <SiteJobForm open={modals.showJobForm} siteId={id} onClose={() => toggleModal('showJobForm')} />}
      {abilities.can('CRUD') && type === 'customer' && isRemediation && (
        <ReadyForPickupModal
          open={modals.showReadyForPickup}
          siteId={id}
          currentReady={details?.ready_for_pickup === true}
          currentPickupDate={details?.pickup_date}
          onClose={() => toggleModal('showReadyForPickup')}
        />
      )}
      <ConfirmDialog
        open={modals.showClearReady}
        title="Clear ready for pickup?"
        confirmLabel="Clear"
        confirmColor="error"
        sending={clearingReady}
        onConfirm={handleClearReady}
        onClose={() => toggleModal('showClearReady')}
      >
        This unmarks the site as ready for pickup and removes its scheduled pickup date.
      </ConfirmDialog>
    </React.Fragment>
  );
};
